import { useMemo, useState } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, WEEKDAYS_SK_SHORT, WEEKDAYS_SK } from "@/lib/app";
import { Warning, ArrowDown, ArrowUp, CheckCircle } from "@phosphor-icons/react";
import PinButton from "@/components/PinButton";
import PeriodOverrideBar from "@/components/PeriodOverrideBar";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STAFFING_LABELS = {
  ok: { label: "OK", color: "var(--success)", bg: "var(--success-bg)" },
  overstaffed: { label: "Prepersonálne", color: "var(--warning)", bg: "var(--warning-bg)" },
  understaffed_demand: { label: "Pridať osobu", color: "var(--critical)", bg: "var(--critical-bg)" },
  understaffed_min: { label: "Pod min. obsluhou", color: "var(--critical)", bg: "var(--critical-bg)" },
  sparse: { label: "Riedky výskyt", color: "var(--text-muted)", bg: "var(--bg-page)" },
};

const PERF_LABELS = {
  good: { label: "Vyhovuje", color: "var(--success)", bg: "var(--success-bg)" },
  warn: { label: "Slabší", color: "var(--warning)", bg: "var(--warning-bg)" },
  bad: { label: "Pod normou", color: "var(--critical)", bg: "var(--critical-bg)" },
  sparse: { label: "Riedky", color: "var(--text-muted)", bg: "var(--bg-page)" },
};

export default function Efficiency({ storeId, period }) {
  const [localPeriod, setLocalPeriod] = useState(period);
  const qs = `period_from=${localPeriod?.from || ""}&period_to=${localPeriod?.to || ""}`;
  const { data } = useSWR(`/stores/${storeId}/analytics/efficiency?${qs}`, fetcher);
  const [filter, setFilter] = useState("all");
  const [hideSparse, setHideSparse] = useState(true);

  const grid = useMemo(() => {
    if (!data?.slots) return null;
    // build matrix: rows = hours, cols = weekdays (0-6)
    const hours = Array.from(new Set(data.slots.map((s) => s.hour))).sort((a, b) => a - b);
    const m = {};
    for (const h of hours) {
      m[h] = {};
      for (let w = 0; w < 7; w++) m[h][w] = null;
    }
    for (const s of data.slots) m[s.hour][s.weekday] = s;
    return { hours, matrix: m };
  }, [data]);

  if (!data) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie efektívnosti" testIdPrefix="efficiency-period" />
      <div className="text-[var(--text-secondary)]">Načítavam...</div>
    </div>
  );
  if (data.empty) return (
    <div className="space-y-3">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie efektívnosti" testIdPrefix="efficiency-period" />
      <div className="card-flat p-8 text-center text-[var(--text-secondary)]">V tomto období nie sú dáta.</div>
    </div>
  );

  const filteredSlots = data.slots.filter((s) => {
    if (hideSparse && s.is_sparse) return false;
    if (filter === "all") return true;
    if (filter === "bad") return s.perf_status === "bad" || (s.staffing !== "ok" && s.staffing !== "sparse");
    if (filter === "sparse") return s.is_sparse;
    return s.perf_status === filter;
  });
  // Group by weekday then hour for readable narrative (Po 7-8 → Po 8-9 → … → Ut 7-8 …)
  filteredSlots.sort((a, b) => (a.weekday - b.weekday) || (a.hour - b.hour));

  const issuesCount = data.slots.filter((s) => s.staffing !== "ok" && s.staffing !== "sparse").length;
  const goodCount = data.slots.filter((s) => s.perf_status === "good").length;
  const sparseCount = data.slots.filter((s) => s.is_sparse).length;

  return (
    <div className="space-y-5">
      <PeriodOverrideBar globalPeriod={period} onChange={setLocalPeriod} label="Obdobie efektívnosti" testIdPrefix="efficiency-period" />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-flat p-5">
          <p className="label-eyebrow mb-2">Otvorené sloty</p>
          <p className="font-display text-3xl font-bold tabular">{data.slots.length}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">deň×hodina kombinácií</p>
        </div>
        <div className="card-flat p-5">
          <p className="label-eyebrow mb-2">Slotov s problémom</p>
          <p className="font-display text-3xl font-bold tabular text-[var(--critical)]">{issuesCount}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">prepersonálne / pod min. / vysoký dopyt</p>
        </div>
        <div className="card-flat p-5">
          <p className="label-eyebrow mb-2">Vyhovujúce slotov</p>
          <p className="font-display text-3xl font-bold tabular text-[var(--success)]">{goodCount}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">€/os.hod ≥ požadovaný výkon</p>
        </div>
        <div className="card-flat p-5">
          <p className="label-eyebrow mb-2">Riedke sloty</p>
          <p className="font-display text-3xl font-bold tabular text-[var(--text-muted)]">{sparseCount}</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">otvorené v &lt; 50 % daných dní</p>
        </div>
      </div>

      <div className="card-flat p-5">
        <h3 className="font-display text-base font-semibold mb-1">Heatmapa €/osobohodina (deň × hodina)</h3>
        <p className="text-xs text-[var(--text-secondary)] mb-4">
          Priemerná efektivita pre každý slot. Požadovaný výkon: <strong className="tabular">{fmtEur(data.required_performance)}</strong>.
          Sivé bunky označené „riedky&rdquo; znamenajú &lt; 50 % výskytov daného dňa — €/os.hod by tu bolo nereprezentatívne, preto sa nezobrazuje. Detail na hover.
        </p>

        <div className="overflow-x-auto scroll-area">
          <table className="min-w-max">
            <thead>
              <tr>
                <th className="!bg-transparent">Hod.</th>
                {WEEKDAYS_SK.map((w, i) => (
                  <th key={i} className="text-center !bg-transparent">{WEEKDAYS_SK_SHORT[i]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid?.hours.map((h) => (
                <tr key={h}>
                  <td className="font-medium tabular">{h}-{h + 1}</td>
                  {Array.from({ length: 7 }).map((_, w) => {
                    const s = grid.matrix[h][w];
                    if (!s) return <td key={w} className="text-center text-[var(--text-muted)] text-xs">—</td>;
                    const r = s.per_person_hour / data.required_performance;
                    let bg, color;
                    if (s.is_sparse) {
                      bg = "#F1F3F2";
                      color = "#7A857F";
                    } else {
                      bg = r >= 1 ? "#EBF4EE" : r >= 0.7 ? "#FDF3E1" : "#FBEAE7";
                      color = r >= 1 ? "#3E7B52" : r >= 0.7 ? "#C78220" : "#BA3F2B";
                    }
                    return (
                      <td
                        key={w}
                        className="text-center relative group"
                        style={{ background: bg, color }}
                        title={`${s.weekday_name} ${s.hour}-${s.hour+1} · €/os.hod ${Math.round(s.per_person_hour)} · ${s.days_observed}/${s.weekday_days} výskytov (${s.frequency_pct}%) · tržba/deň ${fmtEur(s.revenue_per_weekday)}`}
                      >
                        <div className="absolute top-0.5 right-0.5 z-10">
                          <PinButton
                            storeId={storeId}
                            kind="slot"
                            label={`Heatmapa ${s.weekday_name} ${s.hour}-${s.hour+1}: €/os.hod ${Math.round(s.per_person_hour)}, ${fmtNum(s.employees,1)} os., výskyt ${s.days_observed}/${s.weekday_days}${s.is_sparse ? " (riedky)" : ""}`}
                            data={{ weekday: s.weekday, hour: s.hour, source: "heatmap", ...s }}
                            period={period}
                            testId={`pin-heatmap-${s.weekday}-${s.hour}`}
                          />
                        </div>
                        {s.is_sparse ? (
                          <>
                            <div className="tabular text-[10px] font-semibold opacity-80 leading-tight">riedky</div>
                            <div className="tabular text-[10px] opacity-70">{s.days_observed}/{s.weekday_days}</div>
                          </>
                        ) : (
                          <>
                            <div className="tabular text-sm font-medium">{Math.round(s.per_person_hour)}</div>
                            <div className="tabular text-[10px] opacity-70">{fmtNum(s.employees, 1)} os.</div>
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h3 className="font-display text-sm font-semibold">Sloty s odporúčaním</h3>
            <p className="text-xs text-[var(--text-secondary)]">Zoradené po dňoch v týždni a hodinách — čítaj postupne Po → Ne.</p>
          </div>
          <div className="flex gap-1 flex-wrap items-center">
            {[
              { id: "all", lbl: "Všetko" },
              { id: "bad", lbl: "Problémové" },
              { id: "good", lbl: "Vysoká efektivita" },
              { id: "sparse", lbl: "Riedke" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => {
                  setFilter(f.id);
                  if (f.id === "sparse") setHideSparse(false);
                }}
                data-testid={`eff-filter-${f.id}`}
                className={`px-3 py-1.5 text-xs rounded-md ${filter === f.id ? "bg-[var(--brand)] text-white" : "btn-ghost"}`}
              >
                {f.lbl}
              </button>
            ))}
            <label className="inline-flex items-center gap-1.5 text-xs ml-2 cursor-pointer select-none" data-testid="toggle-hide-sparse">
              <input
                type="checkbox"
                checked={hideSparse}
                onChange={(e) => setHideSparse(e.target.checked)}
                className="accent-[var(--brand)]"
                disabled={filter === "sparse"}
              />
              <span>Skryť riedke sloty</span>
            </label>
          </div>
        </div>
        <div className="overflow-x-auto scroll-area">
          <table>
            <thead>
              <tr>
                <th>Deň</th>
                <th>Hodina</th>
                <th className="text-right">Ľudia ø</th>
                <th className="text-right">Tržba ø</th>
                <th className="text-right">Tržba/deň</th>
                <th className="text-right">€/os.hod</th>
                <th className="text-right" title="Výskyt slotu / celkové výskyty dňa">Výskyt</th>
                <th title={`Semafor: porovnanie €/os.hod proti požadovanému výkonu ${fmtEur(data.required_performance)}`}>Výkon</th>
                <th>Personál</th>
                <th>Odporúčanie</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let lastWd = null;
                return filteredSlots.slice(0, 300).map((s, idx) => {
                  const perf = PERF_LABELS[s.perf_status] || PERF_LABELS.warn;
                  const staff = STAFFING_LABELS[s.staffing] || STAFFING_LABELS.ok;
                  const PerfIcon = s.perf_status === "good" ? CheckCircle : s.perf_status === "sparse" ? Warning : ArrowDown;
                  const StaffIcon = s.staffing === "overstaffed" ? ArrowDown : s.staffing === "understaffed_demand" || s.staffing === "understaffed_min" ? ArrowUp : s.staffing === "sparse" ? Warning : CheckCircle;
                  const newDay = s.weekday !== lastWd;
                  lastWd = s.weekday;
                  return (
                    <tr key={idx} className={`${s.is_sparse ? "opacity-60" : ""} ${newDay ? "border-t-2 border-[var(--border)]" : ""}`}>
                      <td className={newDay ? "font-semibold" : "text-[var(--text-muted)] text-xs"}>{newDay ? WEEKDAYS_SK[s.weekday] : "↳"}</td>
                      <td className="tabular">{s.hour}-{s.hour + 1}</td>
                      <td className="text-right tabular">{fmtNum(s.employees, 1)}</td>
                      <td className="text-right tabular">{fmtEur(s.revenue)}</td>
                      <td className="text-right tabular text-[var(--text-muted)]">{fmtEur(s.revenue_per_weekday)}</td>
                      <td className="text-right tabular font-medium">{fmtEur(s.per_person_hour)}</td>
                      <td className={`text-right tabular ${s.is_sparse ? "text-[var(--warning)] font-medium" : "text-[var(--text-muted)]"}`} title={`${s.frequency_pct}%`}>
                        {s.days_observed}/{s.weekday_days}
                      </td>
                      <td>
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
                          style={{ background: perf.bg, color: perf.color }}
                          title={`€/os.hod ${Math.round(s.per_person_hour)} vs požadovaný ${Math.round(data.required_performance)}`}
                        >
                          <PerfIcon size={12} weight="bold" /> {perf.label}
                        </span>
                      </td>
                      <td>
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded" style={{ background: staff.bg, color: staff.color }}>
                          <StaffIcon size={12} weight="bold" /> {staff.label}
                        </span>
                      </td>
                      <td className="text-xs text-[var(--text-secondary)]">{s.reason || "—"}</td>
                      <td className="text-right">
                        <PinButton
                          storeId={storeId}
                          kind="slot"
                          label={`${WEEKDAYS_SK[s.weekday]} ${s.hour}-${s.hour+1}: ${perf.label} / ${staff.label} (${fmtNum(s.employees,1)} os., €/os.hod ${fmtEur(s.per_person_hour)}, výskyt ${s.days_observed}/${s.weekday_days})${s.reason ? " — " + s.reason : ""}`}
                          data={s}
                          period={period}
                          testId={`pin-slot-${s.weekday}-${s.hour}`}
                        />
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
          {filteredSlots.length === 0 && <div className="text-center text-[var(--text-secondary)] py-6 text-sm">Žiadne sloty pre tento filter.</div>}
        </div>
      </div>
    </div>
  );
}
